import type { CalendarEvent } from '@bp/bp-graphql-types';
import { isProfileClaim, PROFILE_CLAIM } from '@bp/pim-auth-constants';
import { Button, EmptyState, Input, LazyLoader, NoTasksIcon } from '@bp/ui-components';
import { Browser } from '@capacitor/browser';
import { Capacitor } from '@capacitor/core';
import React, { type FC, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { hasAuthParams, useAuth } from 'react-oidc-context';
import { useLocation, useParams } from 'react-router-dom';
import { joinEvent } from 'utils/eventHelper';
import { AboutModal } from '../../components/Modals/AboutModal/AboutModal';
import { PageLayoutPlaceholder } from '../../components/PageLayoutPlaceholder/PageLayoutPlaceholder';
import { backendApi } from '../../utils/backendApi';
import { niceDate } from '../../utils/dateCalculations';
import { showErrorToast } from '../../utils/showErrorToast';

export const JoinPage: FC = () => {
  const { t } = useTranslation();
  const auth = useAuth();
  const location = useLocation();

  const [hasTriedSigning, setHasTriedSigning] = useState(false);
  const [requestedEvent, setRequestedEvent] = useState<CalendarEvent | null>(null);
  const [joinDisplayName, setJoinDisplayName] = useState<string>('');
  const [joinEmail, setJoinEmail] = useState<string>('');
  const [joinProfileUuid, setJoinProfileUuid] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(true);

  // try to sign in silently - or fetch public available event
  useEffect(() => {
    if (auth.isAuthenticated) {
      setHasTriedSigning(true);
    }
    if (!hasAuthParams() && !auth.isAuthenticated && !auth.activeNavigator && !auth.isLoading && !hasTriedSigning) {
      auth.signinSilent().finally(() => setHasTriedSigning(true));
    }
  }, [auth, hasTriedSigning]);

  const profile = useMemo(() => {
    const profile = auth.user?.profile[PROFILE_CLAIM];
    if (!profile || !isProfileClaim(profile)) {
      return undefined;
    }
    return profile;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth]);

  const [showAbout, setShowAbout] = useState<boolean>(false);

  const { conference } = useParams();

  useEffect(() => {
    setLoading(true);
    if (hasTriedSigning) {
      backendApi
        .findConference(conference ?? '')
        .then((response) => {
          if (response.ok) {
            response.json().then((data) => setRequestedEvent(data));
            setJoinEmail(profile?.email ?? '');
            setJoinDisplayName(profile?.displayName ?? '');
            setJoinProfileUuid(profile?.uuid ?? '');
            setLoading(false);
          }
        })
        .finally(() => {
          setRequestedEvent(null);
          setLoading(false);
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conference, hasTriedSigning, profile]);

  const login = async () => {
    void auth.signinRedirect({ state: { returnUrl: location.pathname } });
  };

  const join = async () => {
    if (requestedEvent && conference) {
      const response = await joinEvent(conference, {
        displayName: joinDisplayName,
        email: joinEmail,
        uuid: joinProfileUuid,
        organization: { uuid: '', name: '' },
      });

      const url = await response.text();

      if (Capacitor.isNativePlatform()) {
        Browser.open({ url });
      } else {
        window.open(url, '_self');
      }
    } else {
      showErrorToast(t('common.error'));
    }
  };

  return (
    <PageLayoutPlaceholder alignCenter>
      {loading && <LazyLoader transparent={true} />}

      {!loading && !requestedEvent ? (
        <EmptyState maxWidth='200px' title={t('meetings.noMeeting')} icon={<NoTasksIcon />} />
      ) : (
        <>
          {requestedEvent && (
            <>
              <div className={!requestedEvent?.start ? 'mb-5' : 'mb-2'}>
                {(!loading && requestedEvent?.title) ?? t('meetings.titleNotFound')}
              </div>

              {requestedEvent?.start && <div className='mb-5'>{niceDate(requestedEvent?.start)}</div>}

              <Input
                name={'joinName'}
                label={t('common.name')}
                onChange={(e) => setJoinDisplayName(e.target.value)}
                value={joinDisplayName}
                required={true}
                className={!profile?.email ? 'mb-4' : 'mb-2'}
              />

              {!profile?.email && (
                <Input
                  name={'joinEmail'}
                  label={t('common.email')}
                  required={true}
                  onChange={(e) => setJoinEmail(e.target.value)}
                  value={joinEmail}
                />
              )}

              <Button hierarchy='primary' onClick={join} fullWidth className={'mt-6'}>
                {t('meetings.join')}
              </Button>

              {!auth.isAuthenticated && (
                <Button hierarchy='primary' onClick={login} fullWidth className='mt-6'>
                  {t('auth.login')}
                </Button>
              )}
            </>
          )}
        </>
      )}

      <AboutModal state={showAbout} setState={(value) => setShowAbout(value)} />
    </PageLayoutPlaceholder>
  );
};
